import { useState, useRef, useEffect } from 'react';

// Shared UI helpers for the Kids staff view — ported from the approved demo
// (teal-branded dropdown, color swatch picker, pagination) so the real tabs
// look exactly like what Mal signed off on.

export const KIDS_TEAL = '#6AA292';
export const KIDS_YELLOW = '#FFDB7C';
export const COLOR_PRESETS = ['#6AA292', '#3FA34D', '#3B82C4', '#8E6FBF', '#EC6FAA', '#FF7F50', '#FFD23F', '#E0524A', '#FF9B71', '#202020'];

// Canonical program value — survives the (now-fixed) Sheets date coercion of
// "3-4"/"5-6" in case any un-migrated value slips through.
export function normProgram(v) {
  if (!v) return '';
  const s = String(v).trim();
  const m = s.match(/^\d{4}-(\d{2})-(\d{2})T/);
  if (m) {
    if (m[1] === '03' && m[2] === '04') return '3-4';
    if (m[1] === '05' && m[2] === '06') return '5-6';
    return '';
  }
  return s;
}

// What a program is CALLED on cards: the littles are "Early Childhood".
export function programLabel(program) {
  const p = normProgram(program);
  if (p === '3-4' || p === '5-6') return 'Early Childhood';
  return p || '—';
}

// Default group ordering: youngest first (3s & 4s → 5s & 6s → Elementary by
// grade), so lists read in age order instead of creation order.
const PROGRAM_ORDER = { '3-4': 0, '5-6': 1, 'Elementary': 2 };
function gradeRank(name) {
  const m = String(name || '').match(/(\d+)/);
  return m ? parseInt(m[1], 10) : 99;
}
// Groups whose sortOrder is >= this base were deliberately ordered by staff
// (Manage Groups arrows). Legacy sortOrder values (1..N creation order) are
// ignored so the age-order default holds until someone actually reorders.
export const MANUAL_ORDER_BASE = 100;

export function sortKidsGroups(groups) {
  const list = (groups || []).slice();
  const manual = list.length > 0 && list.every(g => parseInt(g.sortOrder, 10) >= MANUAL_ORDER_BASE);
  if (manual) {
    return list.sort((a, b) => parseInt(a.sortOrder, 10) - parseInt(b.sortOrder, 10));
  }
  return list.sort((a, b) => {
    const pa = PROGRAM_ORDER[normProgram(a.program)] ?? 9;
    const pb = PROGRAM_ORDER[normProgram(b.program)] ?? 9;
    if (pa !== pb) return pa - pb;
    const ga = gradeRank(a.name); const gb = gradeRank(b.name);
    if (ga !== gb) return ga - gb;
    return String(a.name || '').localeCompare(String(b.name || ''));
  });
}

// Pretty-print a phone number: 5129237442 → (512) 923-7442. Leaves anything
// that isn't a plain 10/11-digit number alone.
export function formatPhone(v) {
  if (!v) return '';
  const digits = String(v).replace(/\D/g, '');
  if (digits.length === 10) return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  if (digits.length === 11 && digits[0] === '1') return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  return String(v);
}

// "na" / "N/A" / "none" mean no allergy.
export function cleanAllergy(v) {
  const s = String(v || '').trim();
  if (/^(none|n\/?a|nka|n\.k\.a\.?|no|nothing|nope)[.!]?$/i.test(s)) return '';
  return s;
}

export function textOn(hex) {
  const h = (hex || '').replace('#', '');
  if (h.length < 6) return '#fff';
  const r = parseInt(h.slice(0, 2), 16), g = parseInt(h.slice(2, 4), 16), b = parseInt(h.slice(4, 6), 16);
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.62 ? '#202020' : '#fff';
}

// The fixed Kids session grid — mirrors KIDS_SESSION_SLOTS in the Apps Script.
export const KIDS_SESSIONS = [
  { key: 'Thursday|Evening', title: 'Session 1', day: 'Thursday', period: 'Evening' },
  { key: 'Friday|Morning',   title: 'Session 2', day: 'Friday',   period: 'Morning' },
  { key: 'Friday|Evening',   title: 'Session 3', day: 'Friday',   period: 'Evening' },
  { key: 'Saturday|Morning', title: 'Session 4', day: 'Saturday', period: 'Morning' },
  { key: 'Saturday|Evening', title: 'Session 5', day: 'Saturday', period: 'Evening' },
];

// Branded single-select dropdown (teal) — matches the demo's BrandSelect.
export function BrandSelect({ value, options, labels, onChange, placeholder = '—', width, size = 'md', disabled }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({});
  const ref = useRef();
  function toggle() {
    if (disabled) return;
    if (!open && ref.current) {
      const r = ref.current.getBoundingClientRect();
      // The menu is position:fixed, so page scroll can't reveal it — it must
      // fit the viewport. Clamp its height to the space below the trigger, and
      // flip it above when there's more room up there.
      const below = window.innerHeight - r.bottom - 12;
      const above = r.top - 12;
      const base = { position: 'fixed', left: r.left, minWidth: Math.max(r.width, 120) };
      if (below < 160 && above > below) {
        setPos({ ...base, bottom: window.innerHeight - r.top + 4, maxHeight: Math.min(280, above) });
      } else {
        setPos({ ...base, top: r.bottom + 4, maxHeight: Math.min(280, Math.max(below, 120)) });
      }
    }
    setOpen(o => !o);
  }
  const pad = size === 'sm' ? '5px 8px' : '7px 12px';
  const labelFor = (o) => (labels && labels[o] !== undefined) ? labels[o] : (o || placeholder);
  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-block', width }}>
      <button
        onClick={toggle}
        disabled={disabled}
        style={{ width: width ? '100%' : undefined, fontFamily: 'var(--font-h)', fontSize: 12, fontWeight: 700, padding: pad, borderRadius: 6, border: `1.5px solid ${open ? KIDS_TEAL : 'var(--border)'}`, background: '#fff', color: value ? 'var(--black)' : 'var(--muted)', cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.6 : 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{labelFor(value)}</span>
        <svg width="8" height="8" viewBox="0 0 8 8" fill="none" style={{ flexShrink: 0 }}><path d="M1.5 2.5L4 5L6.5 2.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
      </button>
      {open && (
        <>
          <div onClick={() => setOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 240 }} />
          <div style={{ maxHeight: 280, ...pos, zIndex: 250, background: '#fff', border: '1px solid var(--border)', borderRadius: 6, boxShadow: '0 6px 18px rgba(0,0,0,0.12)', overflowY: 'auto' }}>
            {options.map(o => {
              const c = o === value;
              return (
                <div key={o || '__empty'} onClick={() => { onChange(o); setOpen(false); }} style={{ padding: '8px 14px', cursor: 'pointer', fontFamily: 'var(--font-h)', fontSize: 13, fontWeight: c ? 700 : 500, color: c ? '#2E5A4E' : 'var(--mid)', background: c ? 'rgba(106,162,146,0.12)' : '#fff', borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap' }}>
                  {labelFor(o)}
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

// Color picker swatch — preset grid + custom color input (from the demo).
export function ColorSwatch({ color, onChange, size = 26, disabled }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({});
  const ref = useRef();
  function toggle() {
    if (disabled) return;
    if (!open && ref.current) { const r = ref.current.getBoundingClientRect(); setPos({ position: 'fixed', top: r.bottom + 6, left: r.left }); }
    setOpen(o => !o);
  }
  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-block' }}>
      <button onClick={toggle} title="Change color" style={{ width: size, height: size, borderRadius: '50%', background: color || KIDS_TEAL, border: '2px solid #fff', boxShadow: '0 0 0 1px var(--border)', cursor: disabled ? 'default' : 'pointer', flexShrink: 0 }} />
      {open && (
        <>
          <div onClick={() => setOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 240 }} />
          <div style={{ ...pos, zIndex: 250, background: '#fff', border: '1px solid var(--border)', borderRadius: 8, boxShadow: '0 6px 18px rgba(0,0,0,0.14)', padding: 12, width: 168 }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 8, marginBottom: 10 }}>
              {COLOR_PRESETS.map(c => (
                <button key={c} onClick={() => { onChange(c); setOpen(false); }} style={{ width: 24, height: 24, borderRadius: '50%', background: c, border: color === c ? '2px solid #202020' : '2px solid #fff', boxShadow: '0 0 0 1px var(--border)', cursor: 'pointer' }} />
              ))}
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontFamily: 'var(--font-h)', fontSize: 11, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
              <input type="color" value={color || KIDS_TEAL} onChange={e => onChange(e.target.value)} style={{ width: 28, height: 28, padding: 0, border: 'none', background: 'none', cursor: 'pointer' }} />
              Custom
            </label>
          </div>
        </>
      )}
    </div>
  );
}

// Simple client-side pager. Returns the page slice plus the controls element.
export function usePager(items, pageSize = 25) {
  const [page, setPage] = useState(0);
  const total = items.length;
  const pages = Math.max(1, Math.ceil(total / pageSize));
  const safePage = Math.min(page, pages - 1);
  useEffect(() => { if (page > pages - 1) setPage(0); }, [pages, page]);
  const slice = items.slice(safePage * pageSize, (safePage + 1) * pageSize);

  const controls = pages > 1 ? (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 10, padding: '12px 20px', borderTop: '1px solid var(--border)' }}>
      <span style={{ fontFamily: 'var(--font-b)', fontSize: 12, color: 'var(--muted)' }}>
        {safePage * pageSize + 1}–{Math.min(total, (safePage + 1) * pageSize)} of {total}
      </span>
      <button className="btn btn-xs" onClick={() => setPage(p => Math.max(0, p - 1))} disabled={safePage === 0}>‹ Prev</button>
      <button className="btn btn-xs" onClick={() => setPage(p => Math.min(pages - 1, p + 1))} disabled={safePage >= pages - 1}>Next ›</button>
    </div>
  ) : null;

  return { slice, controls, page: safePage, setPage };
}

// Kids-branded group pill.
export function GroupPill({ name, color }) {
  const bg = color || KIDS_TEAL;
  return (
    <span className="pill" style={{ background: bg, color: textOn(bg), fontSize: 9 }}>{name}</span>
  );
}

// Teal "new in the last 24h" tag.
export function NewTag() {
  return <span className="pill" style={{ background: KIDS_TEAL, color: '#fff', fontSize: 9, marginLeft: 6 }}>New</span>;
}

// Yellow allergy highlight wrapper — TBCO yellow per the meeting ("allergies
// equal yellow").
export function AllergyText({ children }) {
  if (!children) return <span style={{ color: 'var(--subtle)' }}>—</span>;
  return (
    <span style={{ background: KIDS_YELLOW, color: '#202020', borderRadius: 4, padding: '2px 7px', fontFamily: 'var(--font-h)', fontSize: 12, fontWeight: 700 }}>
      {children}
    </span>
  );
}
